import { BalanceType, BalanceTypeTitles } from '@/entities/balances'
import { NewCryptoWithdrawModel } from '@/entities/cryptoWithdraw'
import { CryptoCurrencies, Currencies } from '@/entities/curency'
import Tether from '@/shared/assets/icons/payment/tether.svg?react'
import { BaseCheckbox, BaseDialog, BaseInput, BaseSelect, SelectWithLabel } from '@/shared/components'
import { formatCurrency } from '@/shared/lib/currency'
import { generateGUID } from '@/shared/lib/guid'
import { RootState } from '@/store/store'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

type WithdrawDialogProps = {
  isVisible: boolean
  onClose: () => void
  onSubmit: (data: NewCryptoWithdrawModel) => void
  isLoading?: boolean
}

// Валидация криптокошелька TRC-20
const isValidTronWallet = (wallet: string): boolean => {
  return /^T[A-Za-z1-9]{33}$/.test(wallet)
}

// Форматирование суммы (только числа, точка для десятичных)
const formatAmount = (value: string): string => {
  return value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
}

// Заголовки типов балансов

export const WithdrawDialog = ({
  isVisible,
  onClose,
  onSubmit,
  isLoading,
}: WithdrawDialogProps) => {
  const { balances } = useSelector((state: RootState) => state.balances)
  
  // Состояние для выбранного типа баланса
  const [selectedBalanceType, setSelectedBalanceType] = useState<BalanceType>(BalanceType.Deposit)
  // Получаем баланс для выбранного типа
  const selectedBalanceAmount = balances.find((b) => b.balanceType === selectedBalanceType)?.amount ?? 0

  const [amount, setAmount] = useState('')
  const [wallet, setWallet] = useState('')
  const [errors, setErrors] = useState<{ amount?: string; wallet?: string }>({})
  const [checked, setChecked] = useState(false)
  // Обработка отправки формы
  const handleSubmit = () => {
    const withdrawData: NewCryptoWithdrawModel = {
      requestId: generateGUID(),
      balanceType: selectedBalanceType,
      blockchain: CryptoCurrencies.Tron,
      currency: Currencies.USDT,
      amount: amount,
      wallet: wallet,
      comment: '',
      isBalanceCommission: true,
      callbackUrl: '',
    }

    onSubmit(withdrawData)
  }

  const handleAmountChange = useCallback(
    (value: string) => {
      let formatted = value
      let cleaned = value
      let error: string | undefined = undefined

      // Форматирование суммы
      formatted = formatAmount(value)
      cleaned = formatted

      // Валидация суммы
      const numAmount = parseFloat(cleaned)
      if (!cleaned || isNaN(numAmount)) {
        error = 'Введите корректную сумму'
      } else if (numAmount <= 0) {
        error = 'Сумма должна быть больше 0'
      } else if (numAmount > selectedBalanceAmount) {
        error = 'Сумма не может превышать баланс'
      }

      setAmount(formatted)
      setErrors((prev) => ({ ...prev, amount: error }))
    },
    [selectedBalanceAmount]
  )

  const handleWalletChange = useCallback((value: string) => {
    let formatted = value
    let cleaned = value
    let error: string | undefined = undefined

    // Форматирование кошелька (убираем лишние пробелы!)
    formatted = value.trim()
    cleaned = formatted

    // Валидация кошелька
    if (!cleaned) {
      error = 'Введите адрес кошелька'
    } else if (!isValidTronWallet(cleaned)) {
      error = 'Некорректный адрес TRC-20 кошелька'
    }

    setWallet(formatted)
    setErrors((prev) => ({ ...prev, wallet: error }))
  }, [])

  return (
    <BaseDialog
      isVisible={isVisible}
      onClose={onClose}
      onAction={handleSubmit}
      title="Вывод средств"
      actionButtonName="Создать вывод"
      isLoading={isLoading || !checked}
    >
      <>
        {/* Выбор баланса */}
        <SelectWithLabel
          label="Баланс депозита"
          value={selectedBalanceType}
          options={balances.map((balance) => ({
            label: `${formatCurrency(balance.amount, Currencies.USDT)} - ${BalanceTypeTitles[balance.balanceType]}`,
            value: balance.balanceType,
          }))}
          onChange={(value: string | number | null) => {
            setSelectedBalanceType(value as BalanceType)
          }}
          variant="secondary"
        />

        <BaseInput
          variant="secondary"
          placeholder="Сумма"
          label="Введите сумму вывода"
          rightText="USDT"
          value={amount}
          onChange={(e) => handleAmountChange(e.target.value)}
          error={errors.amount}
        />

        <BaseSelect
          label="Выберите валюту"
          value={CryptoCurrencies.Tron}
          variant="secondary"
          options={[{ label: 'USDT (TRON)', value: CryptoCurrencies.Tron, icon: <Tether /> }]}
          onChange={(value: string | number | null) => console.log('Выбрано:', value)}
          disabled={false}
        />

        <BaseInput
          variant="secondary"
          placeholder="Введите кошелек TRC-20"
          label="Укажите кошелек"
          value={wallet}
          onChange={(e) => handleWalletChange(e.target.value)}
          error={errors.wallet}
        />

        <BaseCheckbox
          checked={checked}
          onChange={setChecked}
          label="Данные введены верно"
          variant="secondary"
        />
      </>
    </BaseDialog>
  )
}
